/**
 * Kundenliste und Kundenakte — `PORTAL-002`.
 *
 * **Die externe Kundennummer steht sichtbar in der Akte**, auch wo sie leer
 * ist. `CLAUDE.md` hält fest, dass beim Kundenstamm die Richtung zur
 * Branchensoftware noch offen ist und `PORTAL-002` die Felder deshalb von
 * Anfang an mitführt. Ein Feld, das nur erscheint, wenn es gefüllt ist,
 * beantwortet die Frage „ist dieser Kunde schon drüben angelegt" nicht — und
 * genau die entscheidet, ob man ihn von Hand überträgt.
 *
 * **Anlegen gehört hierher.** `04` §4.4 weist den Kundenstamm keinem
 * Fremdsystem zu; das Portal führt ihn. Bis zum 05.09.2026 konnte diese
 * Ansicht ihn nur zeigen und durchsuchen — der Anruf eines Neukunden endete
 * vor einer Auswahlliste, in der er nicht stand, und der Auftrag ließ sich
 * nicht anlegen. Ein führendes System, in dem der Datensatz nicht entstehen
 * kann, führt nichts.
 */

import { useMemo, useState } from "react";
import { Link, useParams } from "react-router";

import { einsaetzeZuAuftrag, findePerson, kundeNachNummer } from "@hwc/demo-data";
import { formatResourceRef } from "@hwc/contracts";

import { darfKundenPflegen } from "@hwc/auth";
import { Dialog, Knopf, Zustand } from "@hwc/ui";

import { useSitzung } from "../../src/daten/Sitzung.js";
import { legeKundenAn } from "../../src/daten/vorgaenge.js";
import {
  Einsatzabzeichen,
  Feld,
  Felder,
  Leer,
  Liste,
  Zeile,
  datum,
  zumAuftrag,
  zumKunden,
} from "../../src/bereiche/bausteine.js";

export function Kundenliste() {
  const { bestand, rollen } = useSitzung();
  const [frage, setzeFrage] = useState("");
  const [anlegen, setzeAnlegen] = useState(false);
  const [absage, setzeAbsage] = useState<string | null>(null);
  const [angelegt, setzeAngelegt] = useState<string | null>(null);

  const kunden = useMemo(() => {
    const q = frage.trim().toLowerCase();
    return bestand.kunden
      .filter(
        (k) =>
          !q
          || k.name.toLowerCase().includes(q)
          || k.nummer.toLowerCase().includes(q)
          || k.anschrift.ort.toLowerCase().includes(q)
          || (k.externeKundennummer?.includes(q) ?? false),
      )
      .slice()
      .sort((a, b) => a.name.localeCompare(b.name, "de"));
  }, [bestand, frage]);

  return (
    <>
      <div className="bereichskopf">
        <h1>Kunden</h1>
        <span className="bereichskopf__zusatz">
          {kunden.length} von {bestand.kunden.length}
          {darfKundenPflegen(rollen) && (
            <>
              {" "}
              <Knopf type="button" art="neben" onClick={() => setzeAnlegen(true)}>
                Kunde anlegen
              </Knopf>
            </>
          )}
        </span>
      </div>

      {absage && <Zustand art="warnung" text={absage} />}
      {angelegt && (
        <Zustand art="erfolg" text={`${angelegt} ist angelegt und steht in der Liste.`} />
      )}

      <Dialog
        offen={anlegen}
        beiSchliessen={() => setzeAnlegen(false)}
        titel="Kunde anlegen"
      >
        <NeuerKunde
          beiAnlage={(meldung, fehler) => {
            setzeAbsage(fehler ?? null);
            setzeAngelegt(meldung ?? null);
            if (!fehler) setzeAnlegen(false);
          }}
          beiAbbruch={() => setzeAnlegen(false)}
        />
      </Dialog>

      <input
        className="suchfeld"
        type="search"
        placeholder="Name, Kundennummer oder Ort"
        aria-label="Kunden durchsuchen"
        value={frage}
        onChange={(e) => setzeFrage(e.target.value)}
      />

      {kunden.length === 0 ? (
        <Leer text={`Kein Kunde passt zu „${frage}".`} />
      ) : (
        <Liste>
          {kunden.map((k) => {
            const auftraege = bestand.auftraege.filter(
              (a) => formatResourceRef(a.kunde) === formatResourceRef(k.ref),
            );
            const laufend = auftraege.filter(
              (a) => a.status === "in-arbeit" || a.status === "zugewiesen",
            );
            return (
              <Zeile
                key={k.ref.opaqueId}
                zu={zumKunden(k.nummer)}
                titel={k.name}
                unterzeile={
                  <>
                    {k.nummer} · {k.anschrift.plz} {k.anschrift.ort}
                    {k.externeKundennummer && ` · extern ${k.externeKundennummer}`}
                  </>
                }
                rechts={
                  <>
                    {auftraege.length} {auftraege.length === 1 ? "Auftrag" : "Aufträge"}
                    {laufend.length > 0 && (
                      <>
                        <br />
                        {laufend.length} laufend
                      </>
                    )}
                  </>
                }
              />
            );
          })}
        </Liste>
      )}
    </>
  );
}


/**
 * Das Anlegeformular.
 *
 * **Pflicht sind Name und Ort, sonst nichts.** Ein Anruf bringt oft nur
 * „Bäckerei Hufnagel in Burgebrach"; die Straße kommt beim Einsatz. Ein
 * Pflichtfeld dafür führt hier zu erfundenen Adressen — und eine erfundene
 * Adresse ist schlechter als eine fehlende, weil sie aussieht, als stimme sie.
 *
 * **Die Kundennummer steht nicht im Formular.** Sie zählt hoch, wie die
 * Auftragsnummer. Ein frei eingetipptes Feld wäre der kürzeste Weg zu zwei
 * Akten mit derselben Nummer.
 *
 * **„Nummer darf zum Monteur" ist eine eigene Frage, standardmäßig nein.**
 * `07` §7.9 lädt nur die freigegebene Telefonnummer auf das Monteurhandy —
 * die Durchwahl der Geschäftsführung gehört nicht dorthin. Ein Häkchen, das
 * vorausgewählt ist, ist keine Entscheidung.
 */
function NeuerKunde({
  beiAnlage,
  beiAbbruch,
}: {
  beiAnlage: (meldung: string | null, fehler: string | null) => void;
  beiAbbruch: () => void;
}) {
  const { bestand, person, rollen, fuehreAus } = useSitzung();
  const [name, setzeName] = useState("");
  const [strasse, setzeStrasse] = useState("");
  const [plz, setzePlz] = useState("");
  const [ort, setzeOrt] = useState("");
  const [kontaktname, setzeKontaktname] = useState("");
  const [telefon, setzeTelefon] = useState("");
  const [email, setzeEmail] = useState("");
  const [fuerMonteur, setzeFuerMonteur] = useState(false);
  const [notiz, setzeNotiz] = useState("");

  return (
    <>
      <label className="feld">
        <span className="feld__name">Name</span>
        <input
          className="suchfeld"
          type="text"
          value={name}
          onChange={(e) => setzeName(e.target.value)}
          placeholder="z. B. Bäckerei Hufnagel GmbH"
        />
      </label>
      <label className="feld">
        <span className="feld__name">Straße (freiwillig)</span>
        <input
          className="suchfeld"
          type="text"
          value={strasse}
          onChange={(e) => setzeStrasse(e.target.value)}
        />
      </label>
      <label className="feld">
        <span className="feld__name">Postleitzahl (freiwillig)</span>
        <input
          className="suchfeld"
          type="text"
          inputMode="numeric"
          value={plz}
          onChange={(e) => setzePlz(e.target.value)}
        />
      </label>
      <label className="feld">
        <span className="feld__name">Ort</span>
        <input
          className="suchfeld"
          type="text"
          value={ort}
          onChange={(e) => setzeOrt(e.target.value)}
        />
      </label>

      <h3>Ansprechpartner (freiwillig)</h3>
      <label className="feld">
        <span className="feld__name">Name</span>
        <input
          className="suchfeld"
          type="text"
          value={kontaktname}
          onChange={(e) => setzeKontaktname(e.target.value)}
        />
      </label>
      <label className="feld">
        <span className="feld__name">Telefon</span>
        <input
          className="suchfeld"
          type="tel"
          value={telefon}
          onChange={(e) => setzeTelefon(e.target.value)}
        />
      </label>
      <label className="feld">
        <span className="feld__name">E-Mail</span>
        <input
          className="suchfeld"
          type="email"
          value={email}
          onChange={(e) => setzeEmail(e.target.value)}
        />
      </label>
      <label className="feld feld--kasten">
        <input
          type="checkbox"
          checked={fuerMonteur}
          onChange={(e) => setzeFuerMonteur(e.target.checked)}
        />
        <span>
          Diese Nummer darf dem Monteur angezeigt werden
          <br />
          <span className="leise">
            Nach <code>07</code> §7.9 sieht das Monteurhandy nur die freigegebene
            Nummer. Ohne Häkchen bleibt sie im Büro.
          </span>
        </span>
      </label>

      <label className="feld">
        <span className="feld__name">Notiz (freiwillig)</span>
        <input
          className="suchfeld"
          type="text"
          value={notiz}
          onChange={(e) => setzeNotiz(e.target.value)}
        />
      </label>

      <p className="notiz">
        Die Kundennummer wird vergeben, nicht eingetippt. Herkunft ist{" "}
        <strong>Portal</strong>; eine externe Kundennummer trägt nach, wer den
        Kunden in der Branchensoftware anlegt.
      </p>

      <div className="filter">
        <Knopf
          type="button"
          art="haupt"
          disabled={!name.trim() || !ort.trim()}
          onClick={() => {
            const kontakt = kontaktname.trim()
              ? {
                  name: kontaktname.trim(),
                  fuerMonteur,
                  ...(telefon.trim() ? { telefon: telefon.trim() } : {}),
                  ...(email.trim() ? { email: email.trim() } : {}),
                }
              : undefined;

            const grund = fuehreAus((v) =>
              legeKundenAn({
                vorgaenge: v,
                bestand,
                name,
                anschrift: {
                  strasse: strasse.trim(),
                  plz: plz.trim(),
                  ort: ort.trim(),
                },
                akteur: person.ref,
                rollen,
                ...(kontakt ? { kontakt } : {}),
                ...(notiz.trim() ? { notiz } : {}),
              }),
            );
            beiAnlage(grund ? null : name.trim(), grund ?? null);
          }}
        >
          Anlegen
        </Knopf>
        <Knopf type="button" art="neben" onClick={beiAbbruch}>
          Abbrechen
        </Knopf>
      </div>
    </>
  );
}

export function Kundenakte() {
  const { nummer } = useParams();
  const { bestand } = useSitzung();

  const kunde = nummer ? kundeNachNummer(bestand, nummer) : undefined;

  if (!kunde) {
    return (
      <>
        <h1>Kunde nicht gefunden</h1>
        <p className="leise">
          Zur Nummer <code>{nummer}</code> gibt es keine Kundenakte.{" "}
          <Link to="/kunden">Zurück zur Liste</Link>.
        </p>
      </>
    );
  }

  const auftraege = bestand.auftraege
    .filter((a) => formatResourceRef(a.kunde) === formatResourceRef(kunde.ref))
    .slice()
    .sort((x, y) => y.nummer.localeCompare(x.nummer));

  const plaene = bestand.wartungsplaene.filter(
    (p) => formatResourceRef(p.kunde) === formatResourceRef(kunde.ref),
  );

  return (
    <>
      <div className="bereichskopf">
        <h1>{kunde.name}</h1>
        <span className="bereichskopf__zusatz">{kunde.nummer}</span>
      </div>

      <Felder>
        <Feld name="Anschrift">
          {kunde.anschrift.strasse}
          <br />
          {kunde.anschrift.plz} {kunde.anschrift.ort}
        </Feld>
        <Feld name="Kunde seit">{datum(kunde.seit)}</Feld>
        {/*
          Beide Felder erscheinen auch leer. Sie beantworten die Frage, ob
          dieser Kunde in der Branchensoftware schon existiert — und die
          Antwort „nein" ist genauso wichtig wie eine Nummer.
        */}
        <Feld name="Externe Kundennummer">
          {kunde.externeKundennummer ?? <span className="leise">nicht gesetzt</span>}
        </Feld>
        <Feld name="Herkunft">
          {kunde.herkunft === "branchensoftware"
            ? "Branchensoftware"
            : kunde.herkunft === "portal"
              ? "Im Portal angelegt"
              : <span className="leise">nicht gesetzt</span>}
        </Feld>
      </Felder>

      {kunde.notiz && (
        <p className="notiz notiz--warnung">
          <strong>Hinweis zum Kunden:</strong> {kunde.notiz}
        </p>
      )}

      <h2>Ansprechpartner</h2>
      <Liste>
        {kunde.kontakte.map((k) => (
          <Zeile
            key={k.name}
            titel={k.name}
            unterzeile={
              <>
                {k.funktion ?? ""}
                {k.telefon ? ` · ${k.telefon}` : ""}
                {k.email ? ` · ${k.email}` : ""}
              </>
            }
            abzeichen={
              k.fuerMonteur ? null : (
                <span className="abzeichen abzeichen--neutral">nicht für Monteure</span>
              )
            }
          />
        ))}
      </Liste>

      <h2>Aufträge</h2>
      {auftraege.length === 0 ? (
        <Leer text="Für diesen Kunden gibt es noch keinen Auftrag." />
      ) : (
        <Liste>
          {auftraege.map((a) => {
            const einsaetze = einsaetzeZuAuftrag(bestand, a.ref);
            const laufend = einsaetze.find(
              (e) => e.zustand !== "geprueft" && e.zustand !== "storniert",
            );
            return (
              <Zeile
                key={a.ref.opaqueId}
                zu={zumAuftrag(a.nummer)}
                titel={`${a.nummer} — ${a.titel}`}
                unterzeile={
                  <>
                    {a.gewerk ?? ""} · {findePerson(bestand, a.verantwortlich)?.name}
                  </>
                }
                abzeichen={laufend ? <Einsatzabzeichen zustand={laufend.zustand} /> : null}
                rechts={datum(a.angelegt)}
              />
            );
          })}
        </Liste>
      )}

      {plaene.length > 0 && (
        <>
          <h2>Wartungspläne</h2>
          <Liste>
            {plaene.map((p) => (
              <Zeile
                key={p.ref.opaqueId}
                titel={p.titel}
                unterzeile={
                  <>
                    alle {p.intervallMonate} Monate · Vorlauf {p.vorlaufTage} Tage ·{" "}
                    {findePerson(bestand, p.zustaendig)?.name}
                  </>
                }
                abzeichen={
                  p.status === "aktiv" ? null : (
                    <span className="abzeichen abzeichen--neutral">{p.status}</span>
                  )
                }
                rechts={`fällig ${datum(p.naechsteFaelligkeit)}`}
              />
            ))}
          </Liste>
          <p className="notiz">
            Pausieren, verschieben, überspringen oder nachholen geht in der{" "}
            <Link to="/wartung">Wartungsplanung</Link> (<code>07</code> §7.16).
          </p>
        </>
      )}
    </>
  );
}
